import {
  ColumnDef,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { TextCell } from 'components/table/cells/TextCell'
import { TableComponent } from 'components/table/TableComponent'
import React, { useMemo, useState } from 'react'
import { BaseSensorEntry, InputType } from 'types'

interface AllTagsTableProps {
  filteredTags: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  onToggleTag: (tag: BaseSensorEntry) => void
}

export const AllTagsTable: React.FC<AllTagsTableProps> = ({
  filteredTags,
  selectedTags,
  onToggleTag,
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  })
  const [sorting, setSorting] = useState<SortingState>([])

  const rowSelection = useMemo(() => {
    const selection: RowSelectionState = {}
    selectedTags.forEach((selectedTag) => {
      const index = filteredTags.findIndex((tag) => tag.id === selectedTag.id)
      if (index !== -1) {
        selection[selectedTag.id] = true
      }
    })
    return selection
  }, [selectedTags, filteredTags])

  const handleRowSelectionChange = (
    updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    const newSelection =
      typeof updater === 'function' ? updater(rowSelection) : updater

    const previousSelectedIds = new Set(Object.keys(rowSelection))
    const newSelectedIds = new Set(Object.keys(newSelection))

    filteredTags.forEach((tag) => {
      const wasSelected = previousSelectedIds.has(tag.id)
      const isSelected = newSelectedIds.has(tag.id)

      if (wasSelected !== isSelected) {
        onToggleTag(tag)
      }
    })
  }

  const handleRowClick = (
    row: Row<BaseSensorEntry>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (
      event.target instanceof HTMLInputElement &&
      (event.target.type === 'checkbox' ||
        event.target.closest('input[type="checkbox"]'))
    ) {
      return
    }

    const toggleHandler = row.getToggleSelectedHandler()
    toggleHandler(event)
  }

  const columns = useMemo<ColumnDef<BaseSensorEntry>[]>(
    () => [
      {
        accessorKey: 'data.name',
        id: 'name',
        header: 'Tag Name',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.original.data.name ?? ''} truncate />
        ),
      },
      {
        accessorKey: 'data.description',
        id: 'description',
        header: 'Description',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.original.data.description ?? ''} truncate />
        ),
      },
    ],
    []
  )

  return (
    <TableComponent
      data={filteredTags}
      isLoadingData={false}
      columns={columns}
      footerName='Tags'
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      showFilterBar={false}
      showFooter={true}
      enableRowSelection={true}
      rowSelection={rowSelection}
      onRowSelectionChange={handleRowSelectionChange}
      getRowId={(row) => row.id}
      onRowClick={handleRowClick}
    />
  )
}
